<template>
  <template v-if="isFloatingScreenShare">
    <div
      class="pointer-events-none fixed inset-0 z-[70] border-[2.5px] border-solid border-transparent [border-image:linear-gradient(135deg,#2563EB,#38BDF8,#A78BFA,#2563EB)_1] shadow-[inset_0_0_20px_rgba(59,130,246,0.30),inset_0_0_38px_rgba(167,139,250,0.18)]"
      aria-hidden="true"
    ></div>
    <div
      ref="floatingPanelEl"
      class="fixed left-0 top-0 z-[71] w-[min(calc(100vw-32px),360px)] touch-none select-none rounded-2xl border border-[#4A8EEE]/40 bg-white px-4 py-3 text-[#1E3456] shadow-[0_12px_40px_-8px_rgba(30,52,86,0.28)]"
      :class="isDraggingFloatingPanel ? 'cursor-grabbing' : 'cursor-grab'"
      :style="floatingPanelStyle"
      @pointerdown="startFloatingPanelDrag"
    >
      <div class="flex items-center gap-2">
        <div
          class="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#E0EDFF] text-[#4A8EEE]"
        >
          <MonitorUpIcon class="size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <p
            class="flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold"
          >
            <span class="relative flex size-2">
              <span
                class="absolute inline-flex size-full rounded-full bg-[#4A8EEE] opacity-60 animate-ping"
              ></span>
              <span
                class="relative inline-flex size-2 rounded-full bg-[#4A8EEE]"
              ></span>
            </span>
            Screen sharing active
          </p>
          <p class="truncate text-xs text-gray-500">{{ statusText }}</p>
        </div>
        <button
          type="button"
          class="flex size-9 shrink-0 items-center justify-center rounded-full ring-1 transition-colors cursor-pointer"
          :class="
            live.isMuted.value
              ? 'bg-[#4A8EEE] text-white ring-[#4A8EEE]'
              : 'bg-white text-[#1E3456] ring-[#1E3456]/10 hover:bg-[#E0EDFF]'
          "
          :title="live.isMuted.value ? 'Unmute microphone' : 'Mute microphone'"
          :aria-label="
            live.isMuted.value ? 'Unmute microphone' : 'Mute microphone'
          "
          :aria-pressed="live.isMuted.value"
          @click="live.toggleMute"
        >
          <component
            :is="live.isMuted.value ? MicOffIcon : MicIcon"
            class="size-4"
          />
        </button>
        <SortyReportButton
          button-class="flex size-9 shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-[#4A8EEE] hover:bg-[#E0EDFF] transition-colors cursor-pointer"
          icon-class="size-4"
          @click="reportOpen = true"
        />
        <button
          class="shrink-0 rounded-full bg-red-500/80 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 cursor-pointer"
          @click="close"
        >
          End
        </button>
      </div>
    </div>
  </template>
  <div
    v-else
    class="fixed inset-0 z-[70] flex flex-col bg-gradient-to-b from-[#E0EDFF] via-[#F4F7FB] to-white text-[#1E3456] transition-shadow duration-200"
    :class="{
      'border-[2.5px] border-solid border-transparent [border-image:linear-gradient(135deg,#2563EB,#38BDF8,#A78BFA,#2563EB)_1] shadow-[inset_0_0_20px_rgba(59,130,246,0.30),inset_0_0_38px_rgba(167,139,250,0.18)]':
        isScreenSharingActive,
    }"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4">
      <div class="flex items-center gap-2.5">
        <span class="relative inline-flex">
          <img
            src="@/assets/images/sorty-icon.png"
            alt="sorty"
            class="size-9 drop-shadow-sm"
          />
          <span
            class="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-emerald-500 ring-2 ring-white"
            aria-hidden="true"
          ></span>
        </span>
        <div class="leading-tight">
          <p class="text-sm font-semibold text-[#1E3456]">sorty</p>
          <p class="text-xs text-[#4A8EEE]">
            {{ mode === 'screen' ? 'Screen picker opens next' : 'Voice mode' }}
          </p>
          <p
            v-if="isScreenSharingActive"
            class="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#4A8EEE]/15 px-2 py-0.5 text-[11px] font-medium text-[#4A8EEE]"
          >
            <span class="size-1.5 rounded-full bg-[#4A8EEE]"></span>
            Screen sharing active
          </p>
        </div>
      </div>
      <div class="flex items-center gap-1.5">
        <SortyReportButton
          button-class="grid size-9 place-items-center rounded-full text-gray-400 hover:text-[#4A8EEE] hover:bg-white/70 transition-colors cursor-pointer"
          icon-class="size-4"
          @click="reportOpen = true"
        />
        <button
          class="grid size-9 place-items-center rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-[#1E3456]/5 hover:text-[#1E3456] cursor-pointer"
          title="Close"
          aria-label="Close"
          @click="close"
        >
          <XIcon class="size-4" />
        </button>
      </div>
    </div>

    <!-- Center -->
    <div class="flex flex-1 flex-col items-center justify-center gap-6 px-6">
      <div class="relative flex size-40 items-center justify-center">
        <template v-if="isListening">
          <span
            class="absolute size-40 rounded-full bg-[#4A8EEE]/10 animate-ping"
            style="animation-duration: 2.4s"
            aria-hidden="true"
          ></span>
          <span
            class="absolute size-28 rounded-full bg-[#4A8EEE]/15 animate-ping"
            style="animation-duration: 2.4s; animation-delay: 0.6s"
            aria-hidden="true"
          ></span>
        </template>
        <span
          class="absolute size-20 rounded-full ring-1 ring-[#4A8EEE]/25"
          aria-hidden="true"
        ></span>
        <button
          class="relative flex size-16 items-center justify-center rounded-full text-white shadow-xl transition-transform duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          :class="
            live.isMuted.value
              ? 'bg-gray-400 shadow-gray-400/30'
              : 'bg-[#4A8EEE] shadow-[#4A8EEE]/40'
          "
          :disabled="live.status.value === 'connecting'"
          :aria-label="
            mode === 'screen' ? 'Choose what to share' : 'Start speaking'
          "
          @click="onPrimary"
        >
          <component
            :is="primaryIcon"
            class="size-7 transition-transform"
            :class="{ 'scale-110': live.modelSpeaking.value }"
          />
        </button>
      </div>

      <div
        class="eq flex h-8 items-center gap-1 text-[#4A8EEE]"
        :class="{ 'eq-still': !isListening }"
        aria-hidden="true"
      >
        <span v-for="bar in 14" :key="bar"></span>
      </div>

      <p class="text-sm font-medium text-[#1E3456]/70">{{ statusText }}</p>

      <p
        v-if="live.isMuted.value"
        class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600"
      >
        <MicOffIcon class="size-3.5" />
        Microphone muted
      </p>

      <p
        v-if="live.cutoffWarning.value"
        class="rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700 ring-1 ring-amber-200"
      >
        This session will end soon.
      </p>
      <p
        v-if="live.errorMessage.value"
        class="max-w-sm rounded-lg bg-red-50 px-3 py-1.5 text-center text-xs text-red-600"
      >
        {{ live.errorMessage.value }}
      </p>
      <p
        v-if="mode === 'screen' && live.status.value === 'idle'"
        class="max-w-sm text-center text-xs text-gray-500"
      >
        Your browser will ask what to share. Choose only the sortmyprep tab or
        the window you want sorty to see.
      </p>
    </div>

    <!-- Footer -->
    <div class="flex justify-center gap-3 pb-10">
      <button
        v-if="live.status.value === 'live'"
        type="button"
        class="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium shadow-sm ring-1 transition-colors cursor-pointer"
        :class="
          live.isMuted.value
            ? 'bg-[#4A8EEE] text-white ring-[#4A8EEE]'
            : 'bg-white text-[#1E3456] ring-[#1E3456]/10 hover:bg-[#E0EDFF]'
        "
        :aria-pressed="live.isMuted.value"
        @click="live.toggleMute"
      >
        <component
          :is="live.isMuted.value ? MicIcon : MicOffIcon"
          class="size-4"
        />
        {{ live.isMuted.value ? 'Unmute' : 'Mute' }}
      </button>
      <button
        class="flex items-center gap-2 rounded-full bg-red-500/80 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-500 cursor-pointer"
        @click="close"
      >
        <PhoneOffIcon class="size-4" />
        End {{ mode === 'screen' ? 'screen share' : 'voice chat' }}
      </button>
    </div>
  </div>
  <!-- Ant modals sit above the overlay (z-1000 vs z-70); the session keeps
       running while the student reports. -->
  <SortyReportModal v-model:open="reportOpen" :context="reportContext" />
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import {
  AudioLinesIcon,
  MicIcon,
  MicOffIcon,
  MonitorUpIcon,
  PhoneOffIcon,
  XIcon,
} from 'lucide-vue-next';
import { useGeminiLive } from '@/composables/useGeminiLive';
import SortyReportButton from '@/components/sorty/SortyReportButton.vue';
import SortyReportModal from '@/components/sorty/SortyReportModal.vue';
import { SORTY_REPORT_SURFACE } from '@/utils/sortyReports';
import {
  constrainFloatingPosition,
  defaultFloatingPosition,
} from '@/utils/floatingDrag';

const props = defineProps({
  conversationUuid: { type: String, required: true },
  mode: { type: String, default: 'voice' }, // 'voice' | 'screen'
});
const emit = defineEmits(['close']);

const live = useGeminiLive();
const floatingPanelEl = ref(null);
const floatingPanelPosition = ref({ x: 16, y: 16 });
const isDraggingFloatingPanel = ref(false);
let floatingDragOffset = { x: 0, y: 0 };

const reportOpen = ref(false);

function reportContext() {
  return {
    surface:
      props.mode === 'screen'
        ? SORTY_REPORT_SURFACE.SORTY_AI_SCREEN
        : SORTY_REPORT_SURFACE.SORTY_AI_VOICE,
    links: {
      conversation: props.conversationUuid,
      live_session: live.sessionUuid.value,
    },
    turns: live.transcripts.value,
  };
}

const isScreenSharingActive = computed(
  () => props.mode === 'screen' && live.isScreenSharing.value
);

const isFloatingScreenShare = computed(
  () => props.mode === 'screen' && live.isScreenSharing.value
);

const isListening = computed(
  () => live.status.value === 'live' && !live.isMuted.value
);

const primaryIcon = computed(() =>
  props.mode === 'screen' ? MonitorUpIcon : AudioLinesIcon
);

const floatingPanelStyle = computed(() => ({
  transform: `translate3d(${floatingPanelPosition.value.x}px, ${floatingPanelPosition.value.y}px, 0)`,
}));

const statusText = computed(() => {
  switch (live.status.value) {
    case 'connecting':
      return props.mode === 'screen'
        ? "Opening your browser's screen picker…"
        : 'Connecting…';
    case 'reconnecting':
      return 'Reconnecting…';
    case 'live':
      if (live.isMuted.value) {
        return isScreenSharingActive.value
          ? 'Screen sharing active · microphone muted'
          : 'Microphone muted';
      }
      if (isScreenSharingActive.value) {
        return live.modelSpeaking.value
          ? 'Screen sharing active · sorty is speaking…'
          : 'Screen sharing active';
      }
      return live.modelSpeaking.value ? 'sorty is speaking…' : 'Listening…';
    case 'error':
      return '';
    default:
      return props.mode === 'screen'
        ? 'Tap to choose what to share'
        : 'Tap the button to start speaking';
  }
});

function onPrimary() {
  if (live.status.value === 'idle' || live.status.value === 'error') {
    live.start({
      uuid: props.conversationUuid,
      kind: props.mode === 'screen' ? 'SCREEN' : 'VOICE',
      withScreen: props.mode === 'screen',
    });
  }
}

async function close() {
  await live.stop();
  emit('close');
}

function getFloatingPanelBounds() {
  const rect = floatingPanelEl.value?.getBoundingClientRect();
  return {
    elementWidth: rect?.width || Math.min(window.innerWidth - 32, 360),
    elementHeight: rect?.height || 96,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };
}

function resetFloatingPanelPosition() {
  if (typeof window === 'undefined') return;
  floatingPanelPosition.value = defaultFloatingPosition({
    ...getFloatingPanelBounds(),
    margin: 16,
  });
}

function setConstrainedFloatingPanelPosition(x, y) {
  floatingPanelPosition.value = constrainFloatingPosition({
    x,
    y,
    ...getFloatingPanelBounds(),
    margin: 16,
  });
}

function startFloatingPanelDrag(event) {
  if (event.target?.closest?.('button')) return;
  isDraggingFloatingPanel.value = true;
  floatingDragOffset = {
    x: event.clientX - floatingPanelPosition.value.x,
    y: event.clientY - floatingPanelPosition.value.y,
  };
  event.currentTarget?.setPointerCapture?.(event.pointerId);
  window.addEventListener('pointermove', onFloatingPanelDrag);
  window.addEventListener('pointerup', stopFloatingPanelDrag);
}

function onFloatingPanelDrag(event) {
  if (!isDraggingFloatingPanel.value) return;
  setConstrainedFloatingPanelPosition(
    event.clientX - floatingDragOffset.x,
    event.clientY - floatingDragOffset.y
  );
}

function stopFloatingPanelDrag() {
  isDraggingFloatingPanel.value = false;
  window.removeEventListener('pointermove', onFloatingPanelDrag);
  window.removeEventListener('pointerup', stopFloatingPanelDrag);
}

watch(
  () => live.status.value,
  (status) => {
    if (status === 'ended') emit('close');
  }
);

watch(
  isFloatingScreenShare,
  async (floating) => {
    if (!floating) return;
    await nextTick();
    resetFloatingPanelPosition();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  stopFloatingPanelDrag();
  live.stop();
});
</script>

<style scoped>
/* Equaliser bars: scale from the centre; stilled when not listening. */
.eq span {
  width: 3px;
  height: 100%;
  border-radius: 9999px;
  background: currentColor;
  animation: eq 0.9s ease-in-out infinite;
}
.eq span:nth-child(odd) {
  animation-delay: 0.15s;
}
.eq span:nth-child(3n) {
  animation-delay: 0.3s;
}
.eq span:nth-child(4n) {
  animation-delay: 0.45s;
}
.eq-still span {
  animation: none;
  transform: scaleY(0.25);
  opacity: 0.5;
}
@keyframes eq {
  0%,
  100% {
    transform: scaleY(0.3);
  }
  50% {
    transform: scaleY(1);
  }
}
</style>
